import { Navigate, useParams, useNavigate } from "react-router-dom";
import PageTitle from "../page-header/PageHeader";
import { Card, Tag, Typography, Space, Divider, Statistic, Row, Col, Button } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useState, useEffect } from "react";

const { Title, Text, Paragraph } = Typography;

const DetailPromotionCycle = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const isLogged = Boolean(localStorage.getItem("isLogged"));
	const [cycle, setCycle] = useState(null);

	useEffect(() => {
		// Try to get from localStorage
		const stored = localStorage.getItem("promotionCycles");
		if (stored) {
			try {
				const cycles = JSON.parse(stored);
				const foundCycle = cycles.find((c) => c.id === parseInt(id) || String(c.id) === String(id));
				if (foundCycle) {
					setCycle(foundCycle);
					return;
				}
			} catch (error) {
				console.error("Error parsing cycles:", error);
			}
		}

		// Try to get from selected cycle
		const selected = localStorage.getItem("selectedPromotionCycle");
		if (selected) {
			try {
				const selectedCycle = JSON.parse(selected);
				if (selectedCycle.id === parseInt(id) || String(selectedCycle.id) === String(id)) {
					setCycle(selectedCycle);
				}
			} catch (error) {
				console.error("Error parsing selected cycle:", error);
			}
		}
	}, [id]);

	const getStatusColor = (status) => {
		switch (status) {
			case "Active":
				return "green";
			case "Planning":
				return "blue";
			case "Completed":
				return "purple";
			case "Cancelled":
				return "red";
			default:
				return "default";
		}
	};

	const getTypeColor = (type) => {
		switch (type) {
			case "Annual":
				return "purple";
			case "Semi-Annual":
				return "blue";
			case "Quarterly":
				return "cyan";
			case "Ad-hoc":
				return "orange";
			default:
				return "default";
		}
	};

	if (!isLogged) {
		return <Navigate to={"/admin/auth/login"} replace={true} />;
	}

	if (!cycle) {
		return (
			<div>
				<PageTitle title="Back" />
				<Card>
					<Title level={4}>Promotion Cycle Not Found</Title>
					<Text>The promotion cycle you're looking for doesn't exist.</Text>
				</Card>
			</div>
		);
	}

	return (
		<div style={{ padding: "20px" }}>
			<PageTitle title="Back" />
			<Button
				icon={<ArrowLeftOutlined />}
				onClick={() => navigate("/admin/promotionCycle")}
				className="mb-3"
			>
				Back to Promotion Cycles
			</Button>

			<Card>
				<Space direction="vertical" size="large" style={{ width: "100%" }}>
					<div>
						<Title level={2}>{cycle.cycleName}</Title>
						<Space>
							<Tag color={getTypeColor(cycle.cycleType)}>{cycle.cycleType}</Tag>
							<Tag color={getStatusColor(cycle.status)}>{cycle.status}</Tag>
						</Space>
					</div>

					<Divider>Cycle Information</Divider>

					<Row gutter={16}>
						<Col span={8}>
							<Statistic
								title="Start Date"
								value={cycle.startDate ? dayjs(cycle.startDate).format("DD MMM YYYY") : "-"}
							/>
						</Col>
						<Col span={8}>
							<Statistic
								title="End Date"
								value={cycle.endDate ? dayjs(cycle.endDate).format("DD MMM YYYY") : "-"}
							/>
						</Col>
						<Col span={8}>
							<Statistic
								title="Announcement Date"
								value={cycle.announcementDate ? dayjs(cycle.announcementDate).format("DD MMM YYYY") : "-"}
							/>
						</Col>
					</Row>

					<Row gutter={16} className="mt-4">
						<Col span={8}>
							<Statistic
								title="Budget Allocation"
								value={cycle.budgetAllocation ? `₹${parseFloat(cycle.budgetAllocation).toLocaleString("en-IN")}` : "-"}
								valueStyle={{ color: "#3f8600" }}
							/>
						</Col>
						<Col span={8}>
							<Statistic
								title="Eligible Employees"
								value={cycle.eligibleEmployees || "-"}
								valueStyle={{ color: "#1890ff" }}
							/>
						</Col>
						<Col span={8}>
							<Statistic
								title="Cycle Duration"
								value={
									cycle.startDate && cycle.endDate
										? `${dayjs(cycle.endDate).diff(dayjs(cycle.startDate), "day")} days`
										: "-"
								}
							/>
						</Col>
					</Row>

					<Divider>Promotion Criteria</Divider>

					<Row gutter={16}>
						<Col span={8}>
							<Statistic
								title="Minimum Tenure"
								value={cycle.minTenureMonths ? `${cycle.minTenureMonths} months` : "-"}
							/>
						</Col>
						<Col span={8}>
							<Statistic
								title="Minimum Performance Rating"
								value={cycle.minPerformanceRating ? `${cycle.minPerformanceRating}/5` : "-"}
								valueStyle={{ color: "#faad14" }}
							/>
						</Col>
						<Col span={8}>
							<Statistic
								title="Required Skills Count"
								value={cycle.requiredSkillsCount || "-"}
							/>
						</Col>
					</Row>

					{cycle.promotionCriteria && (
						<div>
							<Title level={5}>Promotion Criteria Details</Title>
							<Paragraph>{cycle.promotionCriteria}</Paragraph>
						</div>
					)}

					{cycle.notes && (
						<div>
							<Divider>Notes</Divider>
							<Paragraph>{cycle.notes}</Paragraph>
						</div>
					)}
				</Space>
			</Card>
		</div>
	);
};

export default DetailPromotionCycle;

